import { NavLink, useLocation } from 'react-router-dom'
import type { Nav, NavHeaderProps, NavLinks } from './type'
import * as Collapsible from '@radix-ui/react-collapsible'
import { cn } from '@/lib/utils'
import { ChevronDown, EllipsisIcon } from 'lucide-react'
import { useState } from 'react'
import { useSideBarContext } from './SideBarProvider'
import { Flex } from '../components/base'

type NavCollapsibleProps = Pick<
  NavLinks,
  'label' | 'sub' | 'roles' | 'to' | 'highlight'
>

export default function NavCollapsible({
  label,
  sub,
  to,
  highlight,
  ...rest
}: NavCollapsibleProps) {
  const { pathname } = useLocation()

  const { isOpen: isSidebarOpen } = useSideBarContext()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const isChildActive =
    !!sub?.find(({ to }) => {
      if (!to) return false
      // 정확한 경로 매칭: 완전히 같거나 실제 하위 경로인 경우만
      return pathname === to || (pathname.startsWith(to + '/') && to !== '/')
    }) ||
    (to && (pathname === to || (pathname.startsWith(to + '/') && to !== '/')))

  const handleOnClick = () => {
    setIsDropdownOpen((prev) => !prev)
  }

  return (
    <Collapsible.Root
      key={`collapsible-${label}`}
      defaultOpen={isChildActive}
      disabled={!sub}
      open={isDropdownOpen}
      onOpenChange={setIsDropdownOpen}
      className="group"
    >
      <Collapsible.Trigger asChild>
        <NavHeader
          key={`header-${label}`}
          isSidebarOpen={isSidebarOpen}
          isRole={true}
          label={label}
          to={to}
          isSub={!!sub}
          highlight={highlight}
          handleOnClick={handleOnClick}
          {...rest}
        />
      </Collapsible.Trigger>
      {sub?.map(({ to, label, roles, icon, highlight }) => (
        <Collapsible.Content
          key={`content-${to}`}
          className="collapsibleDropdown"
        >
          <NavDropdown
            to={to}
            label={label}
            isSidebarOpen={isSidebarOpen}
            roles={roles}
            icon={icon}
            highlight={highlight}
            isRole={true}
          />
        </Collapsible.Content>
      ))}
    </Collapsible.Root>
  )
}

type NavDropdownProps = Pick<Nav, 'label' | 'to' | 'roles' | 'highlight'> & {
  isSidebarOpen: boolean
  isRole?: boolean
  icon?: JSX.Element
}

function getHighlightNavClass(isActive: boolean, highlight?: boolean) {
  if (!highlight) {
    return cn({
      'bg-muted text-primary': isActive,
    })
  }

  return cn('border-l-2 border-rose-400 text-rose-800 bg-rose-50/90', {
    'bg-rose-100 text-rose-900 border-rose-500 font-semibold': isActive,
  })
}

function NavDropdown({
  to,
  label,
  isSidebarOpen,
  isRole,
  icon,
  highlight,
}: NavDropdownProps) {
  if (!isRole) return null

  return (
    <NavLink
      key={to}
      to={to as string}
      className={({ isActive }) =>
        cn(
          'group transition-colors flex items-center gap-3 rounded-lg px-3 text-[14px] py-[10px] font-medium pl-5',
          highlight
            ? 'text-rose-800 hover:bg-rose-100 hover:text-rose-900'
            : 'text-slate-700 hover:bg-muted hover:text-primary',
          getHighlightNavClass(isActive, highlight),
          {
            'justify-center': !isSidebarOpen,
          },
        )
      }
    >
      {isSidebarOpen && <span>{label}</span>}
      {!isSidebarOpen && (icon || <EllipsisIcon size={16} />)}
      {!isSidebarOpen && (
        <div className="pointer-events-none absolute left-[95%] hidden whitespace-nowrap rounded bg-black/70 p-2 leading-none text-white transition-opacity duration-300 ease-in-out group-hover:block">
          {label}
        </div>
      )}
    </NavLink>
  )
}

function NavHeader({
  to,
  label,
  icon,
  isSidebarOpen,
  isRole,
  isSub,
  highlight,
  handleOnClick,
}: NavHeaderProps & { isSub: boolean; handleOnClick: VoidFunction }) {
  const Content = (
    <>
      <span
        className={cn(
          'inline-flex gap-x-2 items-center w-full font-medium tracking-tight text-[16px] transition-colors duration-100  ',
          {
            'justify-center': !isSidebarOpen,
          },
        )}
      >
        {icon}
        {isSidebarOpen && (
          <span className={cn('w-32 truncate text-start')}>{label}</span>
        )}
      </span>
      {isSub && isSidebarOpen && (
        <button className='transition-transform duration-300 group-data-[state="open"]:rotate-180'>
          <ChevronDown width={20} height={20} />
        </button>
      )}
      {!isSidebarOpen && (
        <div className="pointer-events-none absolute left-[95%] hidden whitespace-nowrap rounded bg-black/70 p-2 leading-none text-white transition-opacity duration-300 ease-in-out group-hover:block">
          {label}
        </div>
      )}
    </>
  )

  if (!isRole) return <></>

  if (to) {
    return (
      <NavLink
        to={to}
        className={({ isActive }) =>
          cn(
            'group flex cursor-pointer gap-x-3 items-center h-12 justify-center rounded-lg py-[10px] transition-colors pl-2',
            highlight
              ? 'text-rose-800 hover:bg-rose-100 hover:text-rose-900'
              : 'hover:bg-muted hover:text-primary',
            getHighlightNavClass(isActive, highlight),
            {
              'justify-between': isSub,
            },
          )
        }
        onClick={() => isSub && handleOnClick()}
      >
        {Content}
      </NavLink>
    )
  }

  return (
    <Flex
      as="button"
      onClick={() => isSub && handleOnClick()}
      className={cn(
        'flex cursor-pointer gap-x-2 h-12 items-center w-full justify-center rounded py-[10px] pl-2',
      )}
    >
      {Content}
    </Flex>
  )
}
